import React, { useEffect, useState, useCallback } from 'react';
import api from '../api';

/**
 * Store-credit panel.
 *  - Default (admin): shows balance + add/deduct form + full history.
 *  - readonly mode (customer profile): shows balance + read-only history only.
 *
 * Props:
 *   email     string  required
 *   compact   boolean optional - history collapsed by default
 *   readonly  boolean optional - hide the adjust form (for customer-facing view)
 *   theme     'dark' | 'light'  visual mode (defaults to dark for admin)
 */
const CustomerCreditPanel = ({ email, compact = false, readonly = false, theme = 'dark' }) => {
  const [data, setData] = useState({ balance: 0, history: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [delta, setDelta] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showHistory, setShowHistory] = useState(!compact);

  const load = useCallback(async () => {
    if (!email) return;
    setLoading(true);
    setError('');
    try {
      const res = readonly
        ? await api.getProfileCredit(email)
        : await api.adminGetCustomerCredit(email);
      setData({ balance: res.balance ?? 0, history: res.history ?? [] });
    } catch (e) {
      setError(e.message || 'Failed to load credit');
    } finally {
      setLoading(false);
    }
  }, [email, readonly]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    const amt = parseFloat(delta);
    if (Number.isNaN(amt) || amt === 0) {
      setError('Enter a non-zero amount (negative deducts credit)');
      return;
    }
    if (!reason.trim()) {
      setError('Reason is required');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await api.adminAdjustCustomerCredit(email, amt, reason.trim());
      setDelta('');
      setReason('');
      await load();
    } catch (e) {
      setError(e.message || 'Failed to adjust credit');
    } finally {
      setSubmitting(false);
    }
  };

  const fmtMoney = (n) => `$${Number(n || 0).toFixed(2)}`;
  const fmtDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit',
      });
    } catch { return iso; }
  };

  return (
    <div
      data-testid="customer-credit-panel"
      style={{
        background: '#0F0F0F', color: '#FEFEE3', padding: '20px',
        marginBottom: '20px', border: '3px solid #FFC857',
        boxShadow: '4px 4px 0 0 #000',
        fontFamily: "'Courier New', monospace",
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '14px', flexWrap: 'wrap', gap: '10px' }}>
        <div>
          <h3 style={{ fontSize: '14px', color: '#FFC857', letterSpacing: '0.14em', margin: 0 }}>
            [ STORE CREDIT ]
          </h3>
          <div style={{ fontSize: '10px', color: '#999', marginTop: '4px', letterSpacing: '0.12em' }}>
            Auto-applied at checkout
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '10px', color: '#999', letterSpacing: '0.16em' }}>BALANCE</div>
          <div
            data-testid="credit-balance"
            style={{
              fontSize: '28px', color: data.balance > 0 ? '#4AE54A' : '#FEFEE3',
              fontWeight: 'bold', letterSpacing: '0.04em', lineHeight: 1,
            }}
          >
            {fmtMoney(data.balance)}
          </div>
        </div>
      </div>

      {/* Add / deduct form (admin only) */}
      {!readonly && (
        <div style={{ background: '#000', padding: '14px', border: '2px solid #333', marginBottom: '12px' }}>
          <div style={{ fontSize: '10px', color: '#FFC857', letterSpacing: '0.16em', marginBottom: '10px' }}>
            ADJUST CREDIT
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr auto', gap: '10px', alignItems: 'center' }}>
            <input
              data-testid="credit-delta-input"
              type="number"
              step="0.01"
              value={delta}
              onChange={(e) => setDelta(e.target.value)}
              placeholder="$0.00"
              disabled={submitting}
              style={{
                background: '#000', border: '2px solid #4AE54A', color: '#4AE54A',
                padding: '10px 12px', fontFamily: 'inherit', fontSize: '14px',
                outline: 'none', textAlign: 'right',
              }}
            />
            <input
              data-testid="credit-reason-input"
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason (e.g. Giveaway winner)"
              maxLength={200}
              disabled={submitting}
              style={{
                background: '#000', border: '2px solid #333', color: '#FEFEE3',
                padding: '10px 12px', fontFamily: 'inherit', fontSize: '13px',
                outline: 'none',
              }}
            />
            <button
              data-testid="credit-submit-btn"
              onClick={submit}
              disabled={submitting || !delta || !reason.trim()}
              style={{
                padding: '10px 18px',
                background: submitting || !delta || !reason.trim() ? '#444' : '#FFC857',
                color: '#0a0a0a', border: '3px solid #000',
                boxShadow: '3px 3px 0 0 #000',
                fontFamily: 'inherit', fontWeight: 'bold', fontSize: '12px',
                letterSpacing: '0.14em', cursor: submitting ? 'wait' : 'pointer',
              }}
            >
              {submitting ? '...' : 'APPLY'}
            </button>
          </div>
          <div style={{ fontSize: '10px', color: '#666', marginTop: '8px', letterSpacing: '0.12em' }}>
            Tip: positive amount adds credit, negative deducts (e.g. -25.00).
          </div>
          <div style={{ display: 'flex', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
            {[10, 25, 50, 100].map((amt) => (
              <button
                key={amt}
                type="button"
                onClick={() => setDelta(String(amt))}
                disabled={submitting}
                style={{
                  padding: '4px 10px', background: 'transparent',
                  border: '1px solid #4AE54A', color: '#4AE54A',
                  fontFamily: 'inherit', fontSize: '10px', cursor: 'pointer',
                  letterSpacing: '0.1em',
                }}
              >
                +${amt}
              </button>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div style={{
          background: '#3a1010', color: '#ff8888', padding: '8px 12px',
          fontSize: '11px', marginBottom: '12px', border: '1px solid #ff8888',
        }}>
          {error}
        </div>
      )}

      {/* History */}
      <div>
        <button
          type="button"
          onClick={() => setShowHistory((s) => !s)}
          style={{
            background: 'transparent', border: 'none', color: '#999',
            fontSize: '11px', letterSpacing: '0.18em', fontFamily: 'inherit',
            cursor: 'pointer', padding: 0,
          }}
        >
          {showHistory ? '▼' : '▶'} HISTORY ({data.history.length})
        </button>
        {showHistory && (
          <div data-testid="credit-history" style={{ marginTop: '10px' }}>
            {loading && <div style={{ color: '#666', fontSize: '11px' }}>Loading...</div>}
            {!loading && data.history.length === 0 && (
              <div style={{ color: '#666', fontSize: '11px', padding: '8px 0' }}>
                No credit activity yet.
              </div>
            )}
            {!loading && data.history.length > 0 && (
              <div style={{ maxHeight: '260px', overflowY: 'auto', border: '1px solid #222' }}>
                {data.history.map((h) => {
                  const isAdd = h.delta_cents > 0;
                  return (
                    <div
                      key={h.id}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '90px 1fr 100px',
                        padding: '8px 12px', borderBottom: '1px solid #1a1a1a',
                        background: '#080808', alignItems: 'center', gap: '10px',
                      }}
                    >
                      <div style={{
                        color: isAdd ? '#4AE54A' : '#ff8888', fontWeight: 'bold',
                        fontSize: '13px',
                      }}>
                        {isAdd ? '+' : '-'}{fmtMoney(Math.abs(h.delta))}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ color: '#FEFEE3', fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {h.reason || '—'}
                        </div>
                        <div style={{ color: '#666', fontSize: '10px', letterSpacing: '0.06em' }}>
                          {fmtDate(h.created_at)} · {h.source}
                          {h.order_id ? ` · #${String(h.order_id).slice(-8)}` : ''}
                        </div>
                      </div>
                      <div style={{ color: '#999', fontSize: '11px', textAlign: 'right' }}>
                        {fmtMoney(h.balance_after)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerCreditPanel;
